import type { ControllerConfig, ExperimentConfig, PlantParams } from '../simulation/types';

/**
 * Single source of truth for experiment presets. Every lab loads one of these by id.
 * Bump `version` whenever a preset changes in a way that would change recorded evidence.
 */
export interface Preset {
  id: string;
  version: number;
  title: string;
  description: string;
  config: ExperimentConfig;
}

export const DT = 0.005;
export const Z0 = 2;

export const SPRING_PLANT: PlantParams = { m: 1, g: 9.81, k: 2, b: 1, zs: 2, Tmax: 25 };
export const FREE_PLANT: PlantParams = { m: 1, g: 9.81, k: 0, b: 1, zs: 2, Tmax: 25 };

export const BASE_CONTROLLER: ControllerConfig = {
  kind: 'open-loop',
  ffMode: 'operating-point',
  estimated: null,
  Kp: 0,
  Ki: 0,
  Kd: 0,
  antiWindup: true,
  derivTau: 0,
  bangDelta: 3,
  hysteresis: 0,
  bangInitial: 'low',
  manualInput: 0,
};

function experiment(partial: {
  plant: PlantParams;
  controller?: Partial<ControllerConfig>;
  initial?: { z: number; v: number };
  reference?: ExperimentConfig['reference'];
  disturbance?: ExperimentConfig['disturbance'];
  noise?: ExperimentConfig['noise'];
  duration: number;
}): ExperimentConfig {
  return {
    plant: { ...partial.plant },
    z0: Z0,
    initial: partial.initial ?? { z: Z0, v: 0 },
    reference: partial.reference ?? { initial: Z0, steps: [] },
    disturbance: partial.disturbance ?? { initial: 0, steps: [] },
    controller: { ...BASE_CONTROLLER, ...partial.controller },
    noise: partial.noise ?? { enabled: false, sigma: 0.01, seed: 7 },
    duration: partial.duration,
    dt: DT,
  };
}

/** Capstone nominal challenge: step 2 → 2.5 m at 1 s, 1 N downward load from 8 s onward. */
export const CAPSTONE_REFERENCE = { initial: 2, steps: [{ t: 1, value: 2.5 }] };
export const CAPSTONE_DISTURBANCE = { initial: 0, steps: [{ t: 8, value: -1 }] };
/** Disclosed transfer scenario: same step, a 2 N load from 10 s. */
export const TRANSFER_DISTURBANCE = { initial: 0, steps: [{ t: 10, value: -2 }] };

export const CAPSTONE_CRITERIA = {
  stepTime: 1,
  maxOvershoot: 0.1,
  tolerance: 0.05,
  settleWithin: 5,
  maxSaturation: 0.5,
} as const;

const list: Preset[] = [
  {
    id: 'free-manual',
    version: 1,
    title: 'Free altitude, manual thrust',
    description: 'No spring (k = 0). You set the total thrust T directly. Hover needs T = m g = 9.81 N.',
    config: experiment({
      plant: FREE_PLANT,
      controller: { kind: 'manual', ffMode: 'off', manualInput: 9.81 },
      duration: 15,
    }),
  },
  {
    id: 'spring-snapshot',
    version: 1,
    title: 'The vehicle is tethered to the ground via a spring.',
    description:
      'There is a spring attached attached to the drone.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'manual', ffMode: 'off', manualInput: 12 },
      initial: { z: 2.5, v: 0 },
      duration: 10,
    }),
  },
  {
    id: 'open-loop-load',
    version: 1,
    title: 'Predicted thrust meets a hidden load',
    description: 'Feedforward thrust T_0 holds the tethered vehicle at 2 m. At 3 s a 1 N downward load appears.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'open-loop', ffMode: 'operating-point' },
      disturbance: { initial: 0, steps: [{ t: 3, value: -1 }] },
      duration: 12,
    }),
  },
  {
    id: 'manual-correction',
    version: 1,
    title: 'Correct the load by hand',
    description: 'Same load as before. Feedforward is on. You add a manual correction u_fb while watching the altitude.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'manual', ffMode: 'operating-point', manualInput: 0 },
      disturbance: { initial: 0, steps: [{ t: 3, value: -1 }] },
      duration: 12,
    }),
  },
  {
    id: 'supplied-feedback',
    version: 1,
    title: 'Supplied feedback controller',
    description: 'Same load, with a feedback controller. It measures altitude and corrects automatically. You will build it in Module 4.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'p', ffMode: 'operating-point', Kp: 18 },
      disturbance: { initial: 0, steps: [{ t: 3, value: -1 }] },
      duration: 12,
    }),
  },
  {
    id: 'bang-bang',
    version: 1,
    title: 'Bang-bang climb to 2.5 m',
    description: 'Two thrust levels, T_0 ± 3 N. The target is 2.5 m from the start. Initial switch state: low.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'bang-bang', ffMode: 'operating-point', bangDelta: 3, hysteresis: 0, bangInitial: 'low' },
      reference: { initial: 2.5, steps: [] },
      duration: 10,
    }),
  },
  {
    id: 'p-load',
    version: 1,
    title: 'P control with a load and a step',
    description: 'A 1 N downward load from 1 s, then the target steps from 2 m to 2.5 m at 8 s.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'p', ffMode: 'operating-point', Kp: 4 },
      disturbance: { initial: 0, steps: [{ t: 1, value: -1 }] },
      reference: { initial: 2, steps: [{ t: 8, value: 2.5 }] },
      duration: 15,
    }),
  },
  {
    id: 'damping-release',
    version: 1,
    title: 'Spring release (no feedback)',
    description: 'Stiffer spring k = 4 N/m. Released from 0.25 m above z_0 with feedforward T_0 only. Compare damping b = 1, 4, 8 N·s/m.',
    config: experiment({
      plant: { ...SPRING_PLANT, k: 4, b: 1 },
      controller: { kind: 'open-loop', ffMode: 'operating-point' },
      initial: { z: 2.25, v: 0 },
      duration: 10,
    }),
  },
  {
    id: 'pd-release',
    version: 1,
    title: 'Controller damping (PID with K_i = 0)',
    description: 'Free altitude. K_p = 16 N/m supplies stiffness. Vary K_d. Released from 0.25 m above the target.',
    config: experiment({
      plant: FREE_PLANT,
      controller: { kind: 'pid', ffMode: 'operating-point', Kp: 16, Ki: 0, Kd: 1, derivTau: 0 },
      initial: { z: 2.25, v: 0 },
      duration: 10,
    }),
  },
  {
    id: 'pi-load',
    version: 1,
    title: 'Holding a load: P versus PI',
    description: 'Target held at 2 m. A 1 N downward load from 1 s.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'p', ffMode: 'operating-point', Kp: 4, Ki: 3 },
      disturbance: { initial: 0, steps: [{ t: 1, value: -1 }] },
      duration: 25,
    }),
  },
  {
    id: 'windup',
    version: 1,
    title: 'Infeasible target: windup',
    description:
      'At 1 s the target jumps to 10 m, which needs more than T_max = 25 N against the spring. At 10 s it returns to 4 m. Heavier damping (b = 3 N·s/m) keeps the climb from overshooting.',
    config: experiment({
      plant: { ...SPRING_PLANT, b: 3 },
      controller: { kind: 'pi', ffMode: 'operating-point', Kp: 4, Ki: 3, antiWindup: false },
      reference: { initial: 2, steps: [{ t: 1, value: 10 }, { t: 10, value: 4 }] },
      duration: 25,
    }),
  },
  {
    id: 'pid-step',
    version: 1,
    title: 'PI versus PID: step and load',
    description: 'Target 2 → 2.5 m at 1 s. A 1 N downward load from 8 s. Optional seeded sensor noise.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'pi', ffMode: 'operating-point', Kp: 12, Ki: 6, Kd: 6, derivTau: 0 },
      reference: CAPSTONE_REFERENCE,
      disturbance: CAPSTONE_DISTURBANCE,
      noise: { enabled: false, sigma: 0.01, seed: 7 },
      duration: 15,
    }),
  },
  {
    id: 'capstone',
    version: 1,
    title: 'Capstone challenge',
    description: '20 s. Target 2 → 2.5 m at 1 s. A 1 N downward load from 8 s onward. Tethered preset.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'pid', ffMode: 'operating-point', Kp: 4, Ki: 1, Kd: 1 },
      reference: CAPSTONE_REFERENCE,
      disturbance: CAPSTONE_DISTURBANCE,
      duration: 20,
    }),
  },
  {
    id: 'capstone-transfer',
    version: 1,
    title: 'Capstone transfer test',
    description: 'Disclosed in advance: same step at 1 s, but a 2 N downward load from 10 s.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'pid', ffMode: 'operating-point', Kp: 4, Ki: 1, Kd: 1 },
      reference: CAPSTONE_REFERENCE,
      disturbance: TRANSFER_DISTURBANCE,
      duration: 20,
    }),
  },
  {
    id: 'capstone-free',
    version: 1,
    title: 'Capstone on the spring-free preset',
    description: 'The nominal challenge with k = 0 (free altitude).',
    config: experiment({
      plant: FREE_PLANT,
      controller: { kind: 'pid', ffMode: 'operating-point', Kp: 4, Ki: 1, Kd: 1 },
      reference: CAPSTONE_REFERENCE,
      disturbance: CAPSTONE_DISTURBANCE,
      duration: 20,
    }),
  },
  {
    id: 'sandbox',
    version: 1,
    title: 'Summary',
    description: 'Every controller and parameter is available.',
    config: experiment({
      plant: SPRING_PLANT,
      controller: { kind: 'pid', ffMode: 'operating-point', Kp: 12, Ki: 6, Kd: 6 },
      reference: CAPSTONE_REFERENCE,
      duration: 20,
    }),
  },
];

export const PRESETS: Record<string, Preset> = Object.fromEntries(list.map((p) => [p.id, p]));

export function getPreset(id: string): Preset {
  const p = PRESETS[id];
  if (!p) throw new Error(`Unknown preset ${id}`);
  return p;
}

export function cloneConfig(c: ExperimentConfig): ExperimentConfig {
  return structuredClone(c);
}
